import { useReducer, FunctionComponent, useEffect, useState } from 'react'
import API from '@api/client'
import { ResponseMeta } from '@models/Common'
import ConsentReducer, { ConsentActionType } from '@reducers/ConsentReducer'
import { ConsentContext, initialConsentContext } from '@context/ConsentContext'
import { surveyStorage, SurveyStorageKeys } from '@plugins/storage'
import { ConsentResponse } from '@models/Identity'
import { getHasAcceptedCookies } from '@plugins/cookies'
import { getIntegrationData } from '@utils/integration'
import AppLoader from '@components/AppLoader/AppLoader'
import {
  getAdminPreviewUsername,
  isAdminPreviewSession,
} from '../dev/applyAdminPreviewQuery'

export default function WithConsent<P extends JSX.IntrinsicAttributes>(
  WrappedComponent: FunctionComponent<P>,
): FunctionComponent<P> {
  const ComponentWithConsent: FunctionComponent<P> = (props) => {
    const [isLoading, setIsLoading] = useState(true)
    const [state, dispatch] = useReducer(ConsentReducer, initialConsentContext)

    const initState = async () => {
      setIsLoading(true)
      await checkConsents()
      setIsLoading(false)
    }

    const checkConsents = async () => {
      const storedConsent = await surveyStorage.getItem<ConsentResponse>(SurveyStorageKeys.CONSENT_DATA)

      if (storedConsent) {
        dispatch({ type: ConsentActionType.ConsentSuccess, payload: storedConsent })
      }
    }

    const sendConsents = async (surveyId: string) => {
      const integrationData = getIntegrationData()
      const adminPreview = isAdminPreviewSession()
      const adminUsername = getAdminPreviewUsername() || undefined

      dispatch({ type: ConsentActionType.ConsentPending })

      return API.giveConsent(surveyId, {
        isMajor: adminPreview ? true : state.isMajor,
        hasAcceptedCookies: adminPreview ? true : !!getHasAcceptedCookies(),
        integrationData: adminPreview
          ? {
              ...(integrationData || {}),
              src: 'admin-preview',
              ...(adminUsername ? { adminUsername } : {}),
            }
          : integrationData,
        isAdminPreview: adminPreview || undefined,
        adminUsername: adminPreview ? adminUsername : undefined,
      })
        .then(({ data }) => {
          dispatch({
            type: ConsentActionType.ConsentSuccess,
            payload: {
              visitorId: data.visitorId,
              surveyId: data.surveyId,
            },
          })
        })
        .catch((error: ResponseMeta) => {
          dispatch({ type: ConsentActionType.ConsentFailure, payload: error.message })
        })
    }

    const toggleMajor = () => {
      dispatch({ type: ConsentActionType.ToggleMajor })
    }
    const toggleConditions = () => {
      dispatch({ type: ConsentActionType.ToggleConditions })
    }
    const toggleAutoFill = () => {
      dispatch({ type: ConsentActionType.ToggleAutoFill })
    }

    const toggleReadConsent = () => {
      dispatch({ type: ConsentActionType.ToggleReadConsent })
    }

    useEffect(() => {
      void initState()
    }, [])

    if (isLoading) {
      return <AppLoader />
    }

    return (
      <ConsentContext.Provider
        value={{
          ...state,
          sendConsents,
          checkConsents,
          toggleMajor,
          toggleConditions,
          toggleAutoFill,
          toggleReadConsent,
        }}>
        <WrappedComponent {...props} />
      </ConsentContext.Provider>
    )
  }
  return ComponentWithConsent
}
